import { isHexColor, isNamedColor } from './colors';
import { isClickAction, isRawRun } from './compile';
import type { ClickEvent, MessageIssue, RunStyle, TellrawMessage } from './types';

/**
 * What stops a composed message from being sent as it stands.
 *
 * The compiler never refuses anything; it writes the best command it can. The
 * rules here catch what would reach players broken or not at all: a colour
 * the game does not know, a link a client will not open, a click command
 * without its slash, and messages so long that chat or RCON would choke on
 * them. The same rules apply to what staff type and to what the assistant
 * proposes, so each issue is one plain sentence either can act on. Imported
 * RawRuns are sent exactly as written and are not checked.
 */

/** Most runs of text one message may hold. */
export const MAX_TEXT_RUNS = 200;
/** Most characters of chat text one message may hold, tooltips not counted. */
export const MAX_TEXT_LENGTH = 2000;

const EMPTY_CLICK: Record<ClickEvent['action'], string> = {
  open_url: 'Add the link to open.',
  run_command: 'Add the command to run.',
  suggest_command: 'Add the command to suggest.',
  copy_to_clipboard: 'Add the text to copy.',
};

function isWebLink(value: string): boolean {
  if (!/^https?:\/\//i.test(value) || /\s/.test(value)) return false;
  try {
    const url = new URL(value);
    return (url.protocol === 'http:' || url.protocol === 'https:') && url.hostname.length > 0;
  } catch {
    return false;
  }
}

function checkStyle(style: RunStyle, run: number, hover: boolean, issues: MessageIssue[]): void {
  const where = hover ? ' in the hover text' : '';
  if (style.color !== null && style.color !== undefined && !isNamedColor(style.color) && !isHexColor(style.color)) {
    issues.push({ run, message: `Use one of the sixteen named colours or a #rrggbb colour${where}.` });
  }
  if (style.gradient === null || style.gradient === undefined) return;
  const stops: unknown[] = Array.isArray(style.gradient) ? style.gradient : [];
  if (stops.length < 2 || stops.length > 6) {
    issues.push({ run, message: `A gradient${where} needs two to six colours.` });
  } else if (!stops.every(isHexColor)) {
    issues.push({ run, message: `Gradient colours${where} must be #rrggbb colours.` });
  }
  if (style.color !== null && style.color !== undefined) {
    issues.push({ run, message: `Text${where} with a gradient cannot also have a colour.` });
  }
}

function checkClick(click: ClickEvent, run: number, issues: MessageIssue[]): void {
  if (!isClickAction(click.action)) {
    issues.push({ run, message: 'Choose what the click does: open a link, run or suggest a command, or copy text.' });
    return;
  }
  const value = typeof click.value === 'string' ? click.value : '';
  if (!value.trim()) {
    issues.push({ run, message: EMPTY_CLICK[click.action] });
    return;
  }
  if (click.action === 'open_url' && !isWebLink(value)) {
    issues.push({ run, message: 'Links must be full web addresses starting with http:// or https://.' });
  }
  if (click.action === 'run_command' && !value.startsWith('/')) {
    issues.push({ run, message: 'Commands run on click must start with /.' });
  }
}

/** Everything wrong with the message, per run first, then for the message as a whole. */
export function validateMessage(message: TellrawMessage): MessageIssue[] {
  const issues: MessageIssue[] = [];
  const runs = Array.isArray(message?.runs) ? message.runs : [];
  let textRuns = 0;
  let length = 0;
  runs.forEach((run, index) => {
    if (isRawRun(run) || typeof run !== 'object' || run === null) return;
    textRuns += 1;
    length += Array.from(typeof run.text === 'string' ? run.text : '').length;
    checkStyle(run, index, false, issues);
    if (run.click) checkClick(run.click, index, issues);
    const hoverRuns = run.hover && Array.isArray(run.hover.runs) ? run.hover.runs : [];
    for (const hoverRun of hoverRuns) checkStyle(hoverRun, index, true, issues);
  });
  if (textRuns > MAX_TEXT_RUNS) {
    issues.push({ run: null, message: `A message can hold at most ${MAX_TEXT_RUNS} runs of text, and this one has ${textRuns}.` });
  }
  if (length > MAX_TEXT_LENGTH) {
    issues.push({ run: null, message: `A message can hold at most ${MAX_TEXT_LENGTH} characters, and this one has ${length}.` });
  }
  // Several hover runs can share one problem; say it once per run.
  const seen = new Set<string>();
  return issues.filter(issue => {
    const key = `${issue.run}\u0000${issue.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
